import { EMPTY, from, Observable, Observer } from 'rxjs';
import { expand, filter, map } from 'rxjs/operators';
import { Dom } from '../dom/dom';
import { RpcDialogClient } from '../rpc/dialog/rpc-dialog-client';
import {
    RpcDialogChoiceRequest,
    RpcDialogChoiceResponse,
    RpcDialogConfirmationListRequest,
    RpcDialogConfirmationRequest,
    RpcDialogConfirmationResponse,
    RpcDialogData,
    RpcDialogMessageRequest,
    RpcDialogMessageResponse,
    RpcDialogResult,
    RpcDialogState,
    RpcDialogType
} from '../rpc/dialog/rpc-dialog-model';
import { RpcDialogOverlayData } from '../rpc/overlay/rpc-overlay-model';
import { RpcOverlayOpenClient } from '../rpc/overlay/rpc-overlay-open-client';
import { RpcReportClient } from '../rpc/report/rpc-report-client';
import { RpcReportData } from '../rpc/report/rpc-report-model';
import { Rpc } from '../rpc/rpc';
import { RpcUpdateDataClient } from '../rpc/update-data/rpc-update-data-client';
import { RpcUpdateData } from '../rpc/update-data/rpc-update-data-model';
import { ShellNavigationConnection } from './shell-navigation-connection';

/**
 * Frame connection class.
 */
export class FrameConnection {
    private shellNavigationInternal: ShellNavigationConnection;

    /**
     * Active dialog origina data.
     */
    public activeRpcDialogsOrigins: Map<string, HTMLElement>;

    /**
     * Gets the shell navigation functions.
     */
    public get navigation(): ShellNavigationConnection {
        return this.shellNavigationInternal;
    }

    /**
     * Initializes a new instance of the FrameConnection class.
     *
     * @param rpc the RPC object.
     */
    constructor(private rpc: Rpc) {
        this.activeRpcDialogsOrigins = new Map<string, HTMLElement>();
        this.shellNavigationInternal = new ShellNavigationConnection(this.rpc);
    }

    /**
     * Open a choice dialog and wait for completion through RPC.
     * To close the dialog before user input, unsubscribe the observable.
     *
     * @param request the request object of rpc based dialog message.
     */
    public showDialogChoice(request: RpcDialogChoiceRequest): Observable<RpcDialogChoiceResponse> {
        return this.showDialog<RpcDialogChoiceRequest, RpcDialogChoiceResponse>(request, RpcDialogType.OpenChoiceDialog);
    }

    /**
     * Open a message dialog and wait for completion through RPC.
     * To close the dialog before user input, unsubscribe the observable.
     *
     * @param request the request object of rpc based dialog message.
     */
    public showDialogMessage(request: RpcDialogMessageRequest): Observable<RpcDialogMessageResponse> {
        return this.showDialog<RpcDialogMessageRequest, RpcDialogMessageResponse>(
            request, RpcDialogType.OpenMessageDialog);
    }

    /**
     * Open a confirmation dialog and wait for completion through RPC.
     * To close the dialog before user input, unsubscribe the observable.
     *
     * @param request the request object of rpc based dialog confirmation.
     */
    public showDialogConfirmation(request: RpcDialogConfirmationRequest): Observable<RpcDialogConfirmationResponse> {
        return this.showDialog<RpcDialogConfirmationRequest, RpcDialogConfirmationResponse>(
            request, RpcDialogType.OpenConfirmationDialog);
    }

    /**
     * Open a confirmation list dialog and wait for completion through RPC.
     * To close the dialog before user input, unsubscribe the observable.
     *
     * @param request the request object of rpc based dialog confirmation list.
     */
    public showDialogConfirmationList(request: RpcDialogConfirmationListRequest): Observable<RpcDialogConfirmationResponse> {
        return this.showDialog<RpcDialogConfirmationListRequest, RpcDialogConfirmationResponse>(
            request, RpcDialogType.OpenConfirmationListDialog);
    }

    /**
     * Call rpc for overlay command and create an observable
     * @param overlayData true add overlay, false remove
     * @param id id of dialog
     */
    public overlayOpen(overlayData: boolean, id: string): Observable<void> {
        const data: RpcDialogOverlayData = {
            overlay: overlayData,
            dialogId: id
        };
        return from(RpcOverlayOpenClient.overlayOpen(this.rpc, data));
    }

    /**
     * Report location change of module to shell.
     */
    public report(data: RpcReportData): Promise<void> {
        return RpcReportClient.report(this.rpc, data);
    }

    /**
     * Update data of new connection information to shell.
     *
     * @param name the module name.
     * @param subName the sub name of module.
     * @param data the update data object.
     * @return Promise<void> the promise object.
     */
    public updateData(name: string, subName: string, data: RpcUpdateData): Promise<void> {
        return RpcUpdateDataClient.updateData(this.rpc, name, subName, data);
    }

    /**
     * Open a message dialog and wait for completion through RPC.
     *
     * @param request the request object of rpc based dialog message.
     */
    private showDialog<
        TRequest extends RpcDialogMessageRequest | RpcDialogConfirmationRequest | RpcDialogConfirmationListRequest,
        TResult extends RpcDialogMessageResponse | RpcDialogConfirmationResponse>
        (request: TRequest, type: RpcDialogType): Observable<TResult> {
        const data: RpcDialogData = {
            dialogId: MsftSme.getUniqueId(),
            type: type,
            request
        };
        this.activeRpcDialogsOrigins.set(data.dialogId, document.activeElement as HTMLElement);

        return new Observable((observer: Observer<TResult>) => {
            const subscription = this.openAndLongPolling(data)
                .pipe(
                    map((value: RpcDialogResult) => {
                        switch (value.state) {
                            case RpcDialogState.Closed:
                                this.removeActiveDialog(data.dialogId);
                                observer.next(<TResult>value.response);
                                observer.complete();
                                break;
                            case RpcDialogState.Failed:
                                this.removeActiveDialog(data.dialogId);
                                observer.error(new Error(value.failedMessage));
                                break;
                            case RpcDialogState.ForcedTerminated:
                                this.removeActiveDialog(data.dialogId);
                                observer.error(new Error('forced terminated'));
                                break;
                        }
                    }))
                .subscribe();
            return () => {
                if (!subscription.closed) {
                    // sending close request to the shell/dialog if not closed yet.
                    subscription.unsubscribe();
                    data.request = null;
                    data.type = RpcDialogType.Close;
                    RpcDialogClient.dialog(this.rpc, data);
                    this.removeActiveDialog(data.dialogId);
                }
            };
        });
    }

    private removeActiveDialog(dialogId: string): void {
        if (this.activeRpcDialogsOrigins.has(dialogId)) {
            const focusOn = this.activeRpcDialogsOrigins.get(dialogId);
            if (focusOn) {
                // setTimeout to give the button elements time to update either as disabled or not
                setTimeout(() => {
                    if ((focusOn as HTMLButtonElement).disabled) {
                        const nextZoneElement = Dom.getNextZoneElement(focusOn);
                        if (nextZoneElement) {
                            nextZoneElement.focus();
                        }
                    } else {
                        focusOn.focus();
                    }
                });
            }
            this.activeRpcDialogsOrigins.delete(dialogId);
        }
    }

    private openAndLongPolling(data: RpcDialogData): Observable<RpcDialogResult> {
        return this.requestDialog(data)
            .pipe(
                expand((result) => {
                    if (result.state === RpcDialogState.Opened) {
                        // status polling...
                        const newData: RpcDialogData = {
                            dialogId: data.dialogId,
                            type: RpcDialogType.PollingStatus
                        };
                        return this.requestDialog(newData);
                    }

                    return EMPTY;
                }),
                filter(result => result.state !== RpcDialogState.Opened));
    }

    private requestDialog(data: RpcDialogData): Observable<RpcDialogResult> {
        return from(RpcDialogClient.dialog(this.rpc, data));
    }
}
